import type { SerializedQuillValue } from "./types";

export type ColorSegment = {
  index: number;
  length: number;
  color: string;
};

export type StyledColorSegment = {
  index: number;
  length: number;
  color: string;
};

export function parseSerializedValue(raw: string): SerializedQuillValue | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && ("html" in parsed || "delta" in parsed)) {
      return parsed as SerializedQuillValue;
    }
  } catch {
    return null;
  }
  return null;
}

export function serializedHtmlContainsTables(html?: string): boolean {
  return typeof html === "string" && /<table[\s>]/i.test(html);
}

export function serializedDeltaContainsTables(deltaLike: unknown): boolean {
  const ops = Array.isArray((deltaLike as { ops?: unknown[] } | null)?.ops)
    ? ((deltaLike as { ops: unknown[] }).ops ?? [])
    : [];

  return ops.some((op) => {
    const attrs = (op as { attributes?: Record<string, unknown> } | null)?.attributes ?? {};
    return typeof attrs.table === "string";
  });
}

export function normalizePlainParagraphSpacingInDelta(deltaLike: unknown): unknown {
  if (!deltaLike || typeof deltaLike !== "object") return deltaLike;

  const rawOps = Array.isArray((deltaLike as { ops?: unknown[] }).ops)
    ? ((deltaLike as { ops: unknown[] }).ops ?? [])
    : null;

  if (!rawOps) return deltaLike;

  const normalizedOps: Array<{ insert?: unknown; attributes?: Record<string, unknown> }> = [];
  let pendingPlainNewlines = 0;

  const pushStringOp = (text: string, attributes?: Record<string, unknown>) => {
    if (!text) return;
    const lastOp = normalizedOps[normalizedOps.length - 1];
    const sameAttrs = JSON.stringify(lastOp?.attributes ?? null) === JSON.stringify(attributes ?? null);
    if (lastOp && typeof lastOp.insert === "string" && sameAttrs) {
      lastOp.insert += text;
      return;
    }
    normalizedOps.push(attributes ? { insert: text, attributes } : { insert: text });
  };

  const flushPlainNewlines = () => {
    if (pendingPlainNewlines <= 0) return;
    pushStringOp("\n");
    pendingPlainNewlines = 0;
  };

  for (const rawOp of rawOps as Array<{ insert?: unknown; attributes?: Record<string, unknown> }>) {
    if (typeof rawOp.insert !== "string" || rawOp.attributes) {
      flushPlainNewlines();
      normalizedOps.push(rawOp.attributes ? { ...rawOp, attributes: { ...rawOp.attributes } } : { ...rawOp });
      continue;
    }

    let textBuffer = "";
    for (const char of rawOp.insert) {
      if (char === "\n") {
        if (textBuffer) {
          flushPlainNewlines();
          pushStringOp(textBuffer);
          textBuffer = "";
        }
        pendingPlainNewlines += 1;
        continue;
      }

      if (pendingPlainNewlines > 0) {
        flushPlainNewlines();
      }
      textBuffer += char;
    }

    if (textBuffer) {
      flushPlainNewlines();
      pushStringOp(textBuffer);
    }
  }

  flushPlainNewlines();

  return {
    ...(deltaLike as Record<string, unknown>),
    ops: normalizedOps,
  };
}

export function extractStyledColorSegmentsFromDelta(
  deltaLike: unknown
): StyledColorSegment[] {
  const ops = Array.isArray((deltaLike as { ops?: unknown[] } | null)?.ops)
    ? ((deltaLike as { ops: unknown[] }).ops ?? [])
    : [];
  const segments: StyledColorSegment[] = [];
  let cursor = 0;
  for (const op of ops as Array<{ insert?: unknown; attributes?: Record<string, unknown> }>) {
    const insert = op?.insert;
    const attrs = (op?.attributes ?? {}) as Record<string, unknown>;
    const opLength = typeof insert === "string" ? insert.length : 1;
    if (opLength <= 0) continue;
    const color = typeof attrs.color === "string" ? attrs.color : "";
    const hasStyledInlineFormat =
      Boolean(attrs.bold) ||
      Boolean(attrs.italic) ||
      Boolean(attrs.underline) ||
      Boolean(attrs.strike);
    if (hasStyledInlineFormat && color && color !== "no-color") {
      segments.push({ index: cursor, length: opLength, color });
    }
    cursor += opLength;
  }
  return segments;
}

export function serializeEditorValue(delta: unknown, html: string): string {
  return JSON.stringify({ delta, html });
}

